<template>
  <div>
    <div
      class="d-flex justify-space-between"
      :class="isInvitationsFormOpen ? 'align-center' : 'align-start'"
    >
      <div>
        <p class="ma-0 text-left font-weight-bold">Invitados</p>
        <v-slide-x-reverse-transition>
          <div v-if="!isInvitationsFormOpen">
            <p v-if="!invitations.length" class="ma-0">
              No hay invitados a este evento
            </p>
            <div
              v-for="{ id, guest, email, status } in invitations"
              :key="id"
              class="d-flex align-center"
              :style="{ gap: '4px' }"
            >
              <v-icon x-small :color="getInvitationStatusColor(status.value)">
                {{ getInvitationStatusIcon(status.value) }}
              </v-icon>
              <p class="ma-0">
                {{ guest?.name ?? email }}
              </p>
            </div>
          </div>
        </v-slide-x-reverse-transition>
      </div>

      <v-tooltip v-if="!disabled && !readOnly" bottom>
        <template #activator="{ on, attrs }">
          <v-btn
            color="#AF64FD"
            icon
            :disabled="loading"
            v-bind="attrs"
            v-on="on"
            @click="
              () => {
                if (readOnly) return;
                isInvitationsFormOpen = !isInvitationsFormOpen;
              }
            "
          >
            <v-icon>
              {{
                isInvitationsFormOpen
                  ? 'mdi-email-edit'
                  : 'mdi-email-edit-outline'
              }}
            </v-icon>
          </v-btn>
        </template>
        <span>Modificar Invitados</span>
      </v-tooltip>
    </div>

    <v-slide-x-reverse-transition>
      <div v-if="isInvitationsFormOpen">
        <v-expansion-panels flat>
          <v-expansion-panel
            v-for="{ id, guest, email, status } in invitations"
            :key="id"
            class="rounded mb-1"
          >
            <v-expansion-panel-header class="pa-3">
              <p class="ma-0">
                {{ guest?.name ?? email }}
                <span v-if="guest?.name" class="caption text--secondary">
                  - {{ email }}
                </span>
              </p>
            </v-expansion-panel-header>
            <v-expansion-panel-content>
              <div class="d-flex align-center justify-space-between">
                <div class="d-flex align-center" :style="{ gap: '6px' }">
                  <v-icon small :color="getInvitationStatusColor(status.value)">
                    {{ getInvitationStatusIcon(status.value) }}
                  </v-icon>
                  <p class="ma-0">Invitación {{ status.label }}</p>
                </div>
                <div class="d-flex align-center justify-space-between">
                  <v-tooltip
                    v-if="checkInvitationIsPending(status.value)"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        color="#1BA13C"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="onResendInvitation(id, guest, email)"
                      >
                        <v-icon>mdi-email-sync-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Reenviar Invitación</span>
                  </v-tooltip>

                  <v-tooltip
                    v-if="checkInvitationIsPending(status.value)"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        color="#E20809"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="onRevokeInvitation(id)"
                      >
                        <v-icon>mdi-email-remove-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Eliminar Invitación</span>
                  </v-tooltip>

                  <v-tooltip
                    v-if="checkInvitationIsAccepted(status.value)"
                    bottom
                  >
                    <template #activator="{ on, attrs }">
                      <v-btn
                        color="#E20809"
                        icon
                        v-bind="attrs"
                        v-on="on"
                        @click="onRevokeInvitation(id)"
                      >
                        <v-icon>mdi-calendar-remove-outline</v-icon>
                      </v-btn>
                    </template>
                    <span>Eliminar del Evento</span>
                  </v-tooltip>
                </div>
              </div>
            </v-expansion-panel-content>
          </v-expansion-panel>
        </v-expansion-panels>

        <v-form ref="form">
          <v-divider />
          <div class="d-flex flex-column" :style="{ gap: '4px' }">
            <v-combobox
              v-model="guests"
              label="Invitados"
              filled
              hide-details="auto"
              hide-no-data
              hide-selected
              hint="Escribe o selecciona un correo electrónico"
              :items="suggestedGuests"
              item-text="name"
              item-disabled="disabled"
              max-width="auto"
              multiple
              persistent-hint
              :rules="[(v) => v.length > 0 || 'Agrega al menos un invitado']"
              small-chips
            >
              <template #selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  style="max-width: 200px"
                  @click="data.select"
                  @click:close="removeGuest(data.item)"
                >
                  <template v-if="typeof data.item === 'object'">
                    <v-avatar
                      left
                      :style="{ backgroundColor: data.item.avatarColor }"
                    >
                      <span class="white--text caption">
                        {{ data.item.avatar }}
                      </span>
                    </v-avatar>
                    <span class="text-truncate">{{ data.item.name }}</span>
                  </template>
                  <template v-else>
                    <span class="text-truncate">{{ data.item }}</span>
                  </template>
                </v-chip>
              </template>

              <template #item="data">
                <template v-if="data.item.header">
                  <v-subheader class="font-weight-bold">
                    {{ data.item.header }}
                  </v-subheader>
                </template>
                <template v-else>
                  <v-list-item-avatar
                    :style="{
                      backgroundColor: data.item.disabled
                        ? '#ccc'
                        : data.item.avatarColor,
                    }"
                  >
                    <span class="white--text">{{ data.item.avatar }}</span>
                  </v-list-item-avatar>
                  <v-list-item-content
                    :class="{ 'text--disabled': data.item.disabled }"
                  >
                    <v-list-item-title v-html="data.item.name" />
                    <v-list-item-subtitle>
                      {{
                        data.item.disabled
                          ? 'Sin correo registrado'
                          : data.item.email
                      }}
                    </v-list-item-subtitle>
                  </v-list-item-content>
                </template>
              </template>
            </v-combobox>

            <v-btn
              color="#E20809"
              dark
              :elevation="0"
              :loading="loading"
              @click="onSaveGuests"
            >
              Guardar
            </v-btn>
          </div>
        </v-form>
      </div>
    </v-slide-x-reverse-transition>
  </div>
</template>

<script>
  import { InvitationStatus } from '@/core/constants/invitation/action';

  const INVITATION_STATUS_CONFIG = {
    [InvitationStatus.PENDING]: {
      icon: 'mdi-clock-outline',
      color: '#F5A623',
    },
    [InvitationStatus.ACCEPTED]: {
      icon: 'mdi-check-circle',
      color: '#1BA13C',
    },
    [InvitationStatus.REJECTED]: {
      icon: 'mdi-close-circle',
      color: '#E20809',
    },
    [InvitationStatus.REVOKED]: {
      icon: 'mdi-circle-off-outline',
      color: '#E20809',
    },
  };

  export default {
    name: 'EventDetailsInvitations',
    props: {
      event: { type: Object, default: null },
      suggestedGuests: { type: Array, default: () => [] },
      loading: { type: Boolean, default: false },
      disabled: { type: Boolean, default: false },
      readOnly: { type: Boolean, default: false },
    },
    data: () => {
      return {
        InvitationStatus,
        guests: [],
        isInvitationsFormOpen: false,
      };
    },
    computed: {
      invitations() {
        return this.event?.invitations ?? [];
      },
    },
    watch: {
      event() {
        this.guests = [];
        this.isInvitationsFormOpen = false;
      },
    },
    methods: {
      checkInvitationIsAccepted(status) {
        return status === InvitationStatus.ACCEPTED;
      },
      checkInvitationIsPending(status) {
        return status === InvitationStatus.PENDING;
      },
      getInvitationStatusIcon(status) {
        return INVITATION_STATUS_CONFIG[status]?.icon ?? 'mdi-help-circle';
      },
      getInvitationStatusColor(status) {
        return INVITATION_STATUS_CONFIG[status]?.color ?? '#9E9E9E';
      },
      removeInvitation(invitation) {
        this.$emit('removeInvitation', invitation);
      },
      removeGuest(guest) {
        const index = this.guests.findIndex((g) =>
          typeof g === 'string' ? g === guest : g.email === guest.email
        );
        if (index >= 0) this.guests.splice(index, 1);
      },
      onResendInvitation(invitationId, guest, email) {
        const payload = {
          invitationId,
          guest: guest
            ? { id: guest.id, email: guest.email ?? email }
            : { id: null, email },
        };
        this.$emit('resendInvitation', payload);
      },
      onRevokeInvitation(invitationId) {
        this.$emit('revokeInvitation', invitationId);
      },
      onSaveGuests() {
        if (!this.$refs.form.validate()) return;

        this.$emit(
          'createInvitation',
          this.guests.map((g) =>
            typeof g === 'string'
              ? { id: null, email: g }
              : { id: g.id ?? null, email: g.email }
          )
        );

        this.guests = [];
        this.isInvitationsFormOpen = false;
      },
    },
  };
</script>

<style scoped>
  ::v-deep .v-list-item__title,
  .v-list-item__subtitle {
    text-align: left;
  }
</style>
